import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';
import type { ISupportNotification } from '../../../models/Support/ISupportNotification';
import { getInitials } from '../../../utils/Support/supportHelpers';
import { Avatar } from '@HoudiniUI/Avatar';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Button } from '../../../web/components-v2/Button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '../../../web/components-v2/Drawer';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components-v2/Popover';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import { CheckGlyph, SettingsGlyph } from '../../pages/AdminSupportInboxPage/inboxIcons';
import { SupportNotificationPreferencesModal } from './SupportNotificationPreferencesModal';
import type { DesktopNotificationPermission } from './useSupportDesktopNotifications';
import {
	deriveSupportNotificationActorName,
	deriveSupportNotificationType,
	getSupportNotificationDisplay,
	SupportNotificationType,
} from './supportNotificationPresentation';

const BELL_GLYPH_PATH =
	'M18 9.8c0-3.1-1.7-5.3-4.5-5.9V3a1.5 1.5 0 0 0-3 0v.9C7.7 4.5 6 6.7 6 9.8v3.5' +
	'l-1.3 2.1A1.1 1.1 0 0 0 5.6 17h12.8a1.1 1.1 0 0 0 .9-1.6L18 13.3V9.8Z' +
	'm-7.7 8.5a1.9 1.9 0 0 0 3.4 0h-3.4Z';

const MIDDLE_MOUSE_BUTTON = 1;

export type SupportNotificationDestination = { kind: 'route'; path: string } | { kind: 'external'; url: string };

export interface SupportNotificationsBellViewProps {
	isOpen: boolean;
	onOpenChange(isOpen: boolean): void;
	notifications: ISupportNotification[];
	unreadCount: number;
	totalCount?: number;
	isLoading?: boolean;
	isError?: boolean;
	isMarkAllReadPending?: boolean;
	getNotificationDestination(notification: ISupportNotification): SupportNotificationDestination | null;
	onNotificationClick(notification: ISupportNotification, options: { opensInNewTab: boolean }): void;
	onMarkAllRead(): void;
	/** Omitted or 'unsupported' hides the desktop notification row. */
	desktopNotificationPermission?: DesktopNotificationPermission;
	onEnableDesktopNotifications?(): void;
}

// The same modifiers react-router's Link hands back to the browser instead of navigating in place.
function isModifiedClick(event: React.MouseEvent) {
	return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

function getNotificationTimestamp(notification: ISupportNotification) {
	return notification.lastActivityAt || notification.creationDate;
}

function formatRelativeTime(value?: string) {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60_000);
	if (diffMinutes < 1) return 'Just now';
	if (diffMinutes < 60) return `${diffMinutes}m ago`;
	const diffHours = Math.floor(diffMinutes / 60);
	if (diffHours < 24) return `${diffHours}h ago`;
	const diffDays = Math.floor(diffHours / 24);
	if (diffDays === 1) return 'Yesterday';
	if (diffDays < 7) return `${diffDays}d ago`;
	return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function isToday(value?: string) {
	if (!value) return false;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return false;
	const now = new Date();
	return (
		date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()
	);
}

function BellGlyph() {
	return (
		<svg viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
			<path d={BELL_GLYPH_PATH} fill='currentColor' />
		</svg>
	);
}

export function SupportNotificationsBellView({
	isOpen,
	onOpenChange,
	notifications,
	unreadCount,
	totalCount = notifications.length,
	isLoading = false,
	isError = false,
	isMarkAllReadPending = false,
	getNotificationDestination,
	onNotificationClick,
	onMarkAllRead,
	desktopNotificationPermission = 'unsupported',
	onEnableDesktopNotifications,
}: SupportNotificationsBellViewProps) {
	const [showPreferences, setShowPreferences] = React.useState(false);
	const isPhoneLayout = useMediaQuery({ maxWidth: 767 });
	const todayNotifications = notifications.filter(n => isToday(getNotificationTimestamp(n)));
	const earlierNotifications = notifications.filter(n => !isToday(getNotificationTimestamp(n)));
	const remainingCount = Math.max(0, totalCount - notifications.length);

	const renderNotification = (notification: ISupportNotification) => {
		const type = deriveSupportNotificationType(notification.title);
		const { accent, Icon, label } = getSupportNotificationDisplay(type);
		const isUnread = !notification.readDate;
		const isMention = type === SupportNotificationType.Mention;
		const count = notification.count ?? 1;
		const timestamp = formatRelativeTime(getNotificationTimestamp(notification));
		const destination = getNotificationDestination(notification);
		const itemStyles = [styles.item, isUnread ? styles.itemUnread : null];

		const content = (
			<>
				<div className={css(styles.avatarWrap)}>
					<Avatar
						imgSrc={notification.iconUrl || undefined}
						altText='Notification actor'
						fallbackText={getInitials(deriveSupportNotificationActorName(notification.title))}
						smallText
						styles={[styles.avatar]}
					/>
					<span className={css(styles.typeBadge, accent)} aria-label={label}>
						<Icon size={10} />
					</span>
				</div>
				<div className={css(styles.itemContent)}>
					<div className={css(styles.itemTitleRow)}>
						<span className={css(styles.itemTitle, !isUnread ? styles.itemTitleRead : null)}>{notification.title}</span>
						{count > 1 && <span className={css(styles.countBadge)}>{count}</span>}
					</div>
					{notification.message &&
						(isMention ? (
							<div className={css(styles.excerpt)}>{notification.message}</div>
						) : (
							<div className={css(styles.message)}>{notification.message}</div>
						))}
					{timestamp && <div className={css(styles.timestamp)}>{timestamp}</div>}
				</div>
				{isUnread && <span className={css(styles.unreadMarker, accent)} />}
			</>
		);

		if (!destination) {
			return (
				<Button
					key={notification.id}
					type='button'
					variant='ghost'
					styles={itemStyles}
					onClick={() => onNotificationClick(notification, { opensInNewTab: false })}
				>
					{content}
				</Button>
			);
		}

		// A real link, so ⌘/Ctrl/Shift-click and middle-click open the ticket in a new tab and leave this one alone.
		// Middle-click fires auxclick rather than click.
		const linkHandlers = {
			onClick: (event: React.MouseEvent) =>
				onNotificationClick(notification, { opensInNewTab: isModifiedClick(event) }),
			onAuxClick: (event: React.MouseEvent) => {
				if (event.button === MIDDLE_MOUSE_BUTTON) onNotificationClick(notification, { opensInNewTab: true });
			},
		};

		return (
			<Button key={notification.id} asChild variant='ghost' styles={itemStyles}>
				{destination.kind === 'route' ? (
					<Link to={destination.path} {...linkHandlers}>
						{content}
					</Link>
				) : (
					<a href={destination.url} {...linkHandlers}>
						{content}
					</a>
				)}
			</Button>
		);
	};

	const trigger = (
		<Button
			type='button'
			variant='ghost'
			size='icon'
			styles={[styles.trigger]}
			aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
			title='Notifications'
		>
			<BellGlyph />
			{unreadCount > 0 && <span className={css(styles.unreadBadge)}>{unreadCount > 9 ? '9+' : unreadCount}</span>}
		</Button>
	);

	const markAllReadButton = (
		<Button
			type='button'
			variant='link'
			size='sm'
			styles={[styles.markAll]}
			disabled={unreadCount === 0 || isMarkAllReadPending}
			onClick={onMarkAllRead}
		>
			<CheckGlyph size={14} />
			Mark all read
		</Button>
	);

	const notificationSettingsButton = (
		<Button
			type='button'
			variant='ghost'
			size='sm'
			styles={[styles.markAll]}
			onClick={() => setShowPreferences(true)}
			title='Email notification settings'
			aria-label='Email notification settings'
		>
			<SettingsGlyph size={14} />
		</Button>
	);

	const desktopNotificationRow =
		desktopNotificationPermission === 'default' && onEnableDesktopNotifications ? (
			<div className={css(styles.desktopNotificationRow)}>
				<span>Get a desktop alert when a notification comes in.</span>
				<Button type='button' variant='outline' size='sm' onClick={onEnableDesktopNotifications}>
					Turn on
				</Button>
			</div>
		) : desktopNotificationPermission === 'denied' ? (
			<div className={css(styles.desktopNotificationRow)}>
				Desktop alerts are blocked. Allow notifications for this site in your browser settings to turn them on.
			</div>
		) : null;

	const list = (
		<div className={css(styles.list)}>
			{isLoading ? (
				<div className={css(styles.state)}>
					<LoadingSpinner type='small' />
				</div>
			) : isError ? (
				<div className={css(styles.state)}>Failed to load notifications.</div>
			) : notifications.length === 0 ? (
				<div className={css(styles.state)}>You're all caught up.</div>
			) : (
				<>
					{todayNotifications.length > 0 && (
						<>
							<div className={css(styles.sectionLabel)}>Today</div>
							{todayNotifications.map(renderNotification)}
						</>
					)}
					{earlierNotifications.length > 0 && (
						<>
							<div className={css(styles.sectionLabel, styles.sectionLabelDivided)}>Earlier</div>
							{earlierNotifications.map(renderNotification)}
						</>
					)}
				</>
			)}
		</div>
	);

	const footer =
		remainingCount > 0 ? (
			<div className={css(styles.footer)}>
				{remainingCount} more notification{remainingCount === 1 ? '' : 's'}
			</div>
		) : null;

	// A phone gets a bottom sheet rather than an anchored popover: the masthead sits at the top of a
	// 390px screen, so an anchored panel wide enough to read either overflows the viewport or flips
	// off-screen above the bell.
	if (isPhoneLayout) {
		return (
			<div className={css(styles.container)}>
				<Drawer open={isOpen} onOpenChange={onOpenChange} showSwipeHandle>
					<DrawerTrigger render={trigger} />
					<DrawerContent styles={[styles.sheet]}>
						<DrawerHeader styles={[styles.sheetHeader]}>
							<DrawerTitle styles={[styles.title]}>Notifications</DrawerTitle>
							{notificationSettingsButton}
							{markAllReadButton}
						</DrawerHeader>
						{desktopNotificationRow}
						{list}
						{footer}
					</DrawerContent>
				</Drawer>
				{showPreferences && <SupportNotificationPreferencesModal open onOpenChange={setShowPreferences} />}
			</div>
		);
	}

	return (
		<div className={css(styles.container)}>
			<Popover open={isOpen} onOpenChange={onOpenChange}>
				<PopoverTrigger asChild>{trigger}</PopoverTrigger>
				<PopoverContent align='end' sideOffset={10} collisionPadding={12} styles={[styles.popover]}>
					<div className={css(styles.header)}>
						<span className={css(styles.title)}>Notifications</span>
						{notificationSettingsButton}
						{markAllReadButton}
					</div>
					{desktopNotificationRow}
					{list}
					{footer}
				</PopoverContent>
			</Popover>
			{showPreferences && <SupportNotificationPreferencesModal open onOpenChange={setShowPreferences} />}
		</div>
	);
}

const styles = StyleSheet.create({
	container: {
		position: 'relative',
		flex: 'none',
		marginLeft: 'auto',
		marginRight: THEME.spacing.sm,
	},
	trigger: {
		position: 'relative',
		width: 36,
		height: 36,
		'@media (max-width: 767px)': { width: 44, height: 44 },
		padding: 0,
		border: 'none',
		borderRadius: 10,
		background: 'transparent',
		color: THEME.colors.text.main,
		cursor: 'pointer',
		':has(*) > svg': {
			width: 24,
			height: 24,
		},
	},
	unreadBadge: {
		position: 'absolute',
		top: 4,
		right: 3,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 15,
		height: 15,
		padding: '0 3px',
		boxSizing: 'border-box',
		borderRadius: 8,
		backgroundColor: THEME.colors.danger.main,
		border: `2px solid ${THEME.colors.background.light}`,
		color: THEME.colors.primary.contrastText,
		fontSize: 9,
		fontWeight: 700,
		lineHeight: 1,
	},
	// Radix reports the room it has under the trigger; without capping to it the panel runs off a
	// short window instead of scrolling its list.
	popover: {
		display: 'flex',
		flexDirection: 'column',
		width: 390,
		maxHeight: 'min(560px, var(--radix-popover-content-available-height, 560px))',
		padding: 0,
		overflow: 'hidden',
	},
	sheet: {
		'--drawer-inset': '0px',
		'--drawer-bleed-background': THEME.colors.background.light,
		'--drawer-content-height': 'min(80dvh, 640px)',
		'--drawer-content-max-height': 'min(80dvh, 640px)',
		borderRadius: `${THEME.spacing.md}px ${THEME.spacing.md}px 0 0`,
		borderBottom: 'none',
	},
	sheetHeader: {
		flex: 'none',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.md}px ${THEME.spacing.sm}px`,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		textAlign: 'left',
	},
	header: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	desktopNotificationRow: {
		flex: 'none',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: SPACE[16],
		padding: `${SPACE[8]}px ${SPACE[24]}px`,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.main,
		fontSize: 12,
		lineHeight: '17px',
		color: THEME.colors.text.light,
	},
	title: {
		fontSize: 15,
		fontWeight: 600,
		color: THEME.colors.text.main,
	},
	markAll: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		height: 'auto',
		padding: 0,
		border: 'none',
		background: 'transparent',
	},
	// The only scroll region in either surface; the header and footer stay put.
	list: {
		flexGrow: 1,
		minHeight: 0,
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	state: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: 130,
		padding: THEME.spacing.lg,
		fontSize: 14,
		color: THEME.colors.text.light,
	},
	sectionLabel: {
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px ${THEME.spacing.xs}px`,
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
		color: THEME.colors.text.light,
	},
	sectionLabelDivided: {
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	item: {
		position: 'relative',
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'flex-start',
		gap: THEME.spacing.md,
		width: '100%',
		height: 'auto',
		// Button's base style is nowrap, which children inherit — a long title then widened the row
		// past the panel and put a horizontal scrollbar under the list.
		whiteSpace: 'normal',
		padding: `12px ${THEME.spacing.md}px`,
		border: 'none',
		borderRadius: 0,
		textAlign: 'left',
		backgroundColor: THEME.colors.background.light,
		cursor: 'pointer',
		':hover': {
			backgroundColor: THEME.colors.background.main,
		},
	},
	itemUnread: {
		backgroundColor: THEME.colors.primary.light,
	},
	avatarWrap: {
		position: 'relative',
		flex: 'none',
		width: 36,
		height: 36,
	},
	avatar: {
		width: 36,
		height: 36,
		fontSize: 12,
		flex: 'none',
	},
	typeBadge: {
		position: 'absolute',
		right: -2,
		bottom: -2,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 18,
		height: 18,
		borderRadius: '50%',
		color: THEME.colors.primary.contrastText,
		border: `2px solid ${THEME.colors.background.light}`,
	},
	itemContent: {
		minWidth: 0,
		flex: 1,
	},
	itemTitleRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: THEME.spacing.sm,
	},
	itemTitle: {
		fontSize: 13,
		fontWeight: 600,
		lineHeight: '18px',
		color: THEME.colors.text.main,
		minWidth: 0,
		overflowWrap: 'anywhere',
	},
	itemTitleRead: {
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.light,
	},
	countBadge: {
		flex: 'none',
		minWidth: 18,
		height: 18,
		padding: '0 5px',
		borderRadius: 9,
		backgroundColor: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
		fontSize: 11,
		fontWeight: 600,
		lineHeight: '18px',
		textAlign: 'center',
	},
	message: {
		marginTop: 2,
		fontSize: 12,
		lineHeight: '17px',
		color: THEME.colors.text.light,
		// @ts-expect-error Aphrodite's display type omits -webkit-box.
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 2,
		overflow: 'hidden',
	},
	excerpt: {
		marginTop: 7,
		padding: '7px 10px',
		borderLeft: `2px solid ${THEME.colors.primary.main}`,
		borderRadius: '0 6px 6px 0',
		backgroundColor: THEME.colors.background.main,
		fontSize: 12,
		lineHeight: '17px',
		color: THEME.colors.text.main,
		// @ts-expect-error Aphrodite's display type omits -webkit-box.
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 2,
		overflow: 'hidden',
	},
	timestamp: {
		marginTop: 6,
		fontSize: 11,
		color: THEME.colors.text.light,
	},
	unreadMarker: {
		flex: 'none',
		alignSelf: 'flex-start',
		marginTop: 5,
		width: 8,
		height: 8,
		borderRadius: '50%',
	},
	footer: {
		flex: 'none',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.lg}px`,
		fontSize: 12,
		color: THEME.colors.text.light,
		borderTop: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.main,
	},
});
